import { inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, map, Observable, of } from 'rxjs';
import {
  ConstructionType,
  ProjectModel,
  PublicProject,
  SalesTaxClass,
  SellingPriceType,
  SitingAnalysis
} from '../models/project.models';
import { API_CONFIG, ApiCallingV2Service } from '@zayan-erp/shared';
import { Project } from '../models/project.models';
import { JobType, Lot, LotRelationship } from '../models/lots.models';
import { City } from '../models/address.models';
import { ModelElevation } from '../models/model-elevations';
import { Elevation } from '../models/elevation.models';
import { MappingLot } from '../models/map.models';
import { ProjectIncentive } from '../models/incentive.models';

@Injectable({
  providedIn: 'root'
})
export class ProjectsService {
  projectModels = signal<ProjectModel[]>([]);
  reloadProjects = new BehaviorSubject<boolean>(false);
  reloadProjects$ = this.reloadProjects.asObservable();
  disabled = signal<boolean>(false);
  imageEmpty = signal<boolean>(false);

  reloadProjectModels = new BehaviorSubject<boolean>(false);
  reloadProjectModels$ = this.reloadProjectModels.asObservable();

  saveModelAssignment = new BehaviorSubject<ModelElevation | null>(null);
  saveModelAssignment$ = this.saveModelAssignment.asObservable();

  changeConstructionType = new BehaviorSubject<string | null>(null);
  changeConstructionType$ = this.changeConstructionType.asObservable();

  selectedProductListId = signal<string | null>(null);

  #http = inject(ApiCallingV2Service);
  #apiConfig = inject(API_CONFIG);
  baseUrl = this.#apiConfig.baseUrlV2;

  createProject(project: Omit<Project, 'id'>): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/Project`,
      { showLoader: true, showMessage: true, message: 'Project created successfully' },
      project
    );
  }

  readProjects(): Observable<Project[]> {
    return this.#http.getData(`${this.baseUrl}/project`, { showLoader: true, showMessage: false });
  }

  updateProject(project: Project): Observable<unknown> {
    return this.#http.putData(`${this.baseUrl}/project/${project.id}`, project, {
      showLoader: true,
      showMessage: true,
      message: 'Project updated successfully'
    });
  }

  updateProjectDetails(
    project: Project,
    projectId: string,
    options?: { showError?: boolean; confirmProductListChange?: boolean }
  ): Observable<unknown> {
    const body = options?.confirmProductListChange ? { ...project, confirmProductListChange: true } : project;
    return this.#http.putData(`${this.baseUrl}/project/${projectId}`, body, {
      showLoader: true,
      showMessage: true,
      message: 'Project updated successfully',
      showError: options?.showError ?? true
    });
  }

  deleteProject(ids: string[]): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/project/bulk-delete`,
      { showLoader: true, showMessage: true, message: 'Project deleted successfully' },
      ids
    );
  }

  upsertProject(projects: Omit<Project, 'id'>[]): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/Project/bulk-upsert`,
      { showLoader: true, showMessage: true, message: 'Projects added successfully' },
      projects
    );
  }

  getProjectLots(
    projectId: string,
    includingInActive: boolean = true,
    lotStatus?: string,
    showLoader = true
  ): Observable<Lot[]> {
    const searchParams = new URLSearchParams();
    searchParams.set('includingInActive', `${includingInActive}`);
    if (lotStatus) {
      searchParams.append('lotStatus', lotStatus);
    }
    return this.#http.getData(`${this.baseUrl}/Project/${projectId}/lots?${searchParams.toString()}`, {
      showLoader,
      showMessage: false
    });
  }

  getProjectById(projectId: string): Observable<Project> {
    return this.#http.getData(`${this.baseUrl}/Project/${projectId}`, { showLoader: true, showMessage: false });
  }

  readConstructionTypes(): Observable<ConstructionType[]> {
    return this.#http.getData(`${this.baseUrl}/ConstructionType`, { showLoader: false, showMessage: false });
  }

  readSellingPriceTypes(): Observable<SellingPriceType[]> {
    return this.#http.getData(`${this.baseUrl}/SellingPriceType`, { showLoader: false, showMessage: false });
  }

  readHardCostJobTypes(): Observable<JobType[]> {
    return this.#http.getData(`${this.baseUrl}/HardCostJobType`, { showLoader: false, showMessage: false });
  }

  readSoftCostJobTypes(): Observable<JobType[]> {
    return this.#http.getData(`${this.baseUrl}/SoftCostJobType`, { showLoader: false, showMessage: false });
  }

  readSalesTaxClasses(): Observable<SalesTaxClass[]> {
    return this.#http.getData(`${this.baseUrl}/SalesTaxClass`, { showLoader: false, showMessage: false });
  }

  readCities(): Observable<City[]> {
    return this.#http.getData(`${this.baseUrl}/City`, { showLoader: false, showMessage: false });
  }

  readCitiesPublic(): Observable<City[]> {
    return this.#http.getData(`${this.baseUrl}/public/cities`, { showLoader: false, showMessage: false });
  }

  assignModelsToProject(projectId: string, modelIds: string[]): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/Project/${projectId}/model`,
      { showLoader: true, showMessage: true, message: 'Models assigned to project successfully' },
      modelIds
    );
  }

  readProjectModels(projectId: string): Observable<any> {
    return this.#http.getData(`${this.baseUrl}/Project/${projectId}/models`, { showLoader: true, showMessage: false });
  }

  readModelsByConstructionType(constructionTypeId?: string): Observable<any> {
    return this.#http.getData(`${this.baseUrl}/Model?constructionTypeId=${constructionTypeId}`, {
      showLoader: true,
      showMessage: false
    });
  }

  removeModelFromProject(projectId: string, modelId: string): Observable<unknown> {
    return this.#http.deleteData(`${this.baseUrl}/Project/${projectId}/model/${modelId}`, {
      showLoader: true,
      showMessage: true,
      message: 'Model removed from project successfully'
    });
  }

  updateModelElevation(projectId: string, projModelId: string, elevation: Elevation[]): Observable<unknown> {
    return this.#http.putData(
      `${this.baseUrl}/Project/${projectId}/model/${projModelId}`,
      { elevations: elevation },
      { showLoader: true, showMessage: true, message: 'Model elevation updated successfully' }
    );
  }

  getProjectDetailPublic(projectId: string): Observable<PublicProject> {
    return this.#http.getData(`${this.baseUrl}/public/projects/${projectId}`, { showLoader: true, showMessage: false });
  }

  getProjectLotMappingPublic(projectId: string, showLoader: boolean = true): Observable<MappingLot[]> {
    return this.#http.getData(`${this.baseUrl}/public/projects/${projectId}/lot-mapping`, {
      showLoader,
      showMessage: false
    });
  }

  getProjectIncentives(projectId: string): Observable<ProjectIncentive[]> {
    return this.#http
      .getData(`${this.baseUrl}/project-incentives/project/${projectId}`, { showLoader: true, showMessage: false })
      .pipe(map((res: any) => res ?? []));
  }

  createProjectIncentive(incentive: ProjectIncentive): Observable<ProjectIncentive> {
    return this.#http.postData(
      `${this.baseUrl}/project-incentives`,
      { showLoader: true, showMessage: true, message: 'Incentive created successfully' },
      incentive
    );
  }

  updateProjectIncentive(incentive: ProjectIncentive): Observable<ProjectIncentive> {
    return this.#http.putData(`${this.baseUrl}/project-incentives/${incentive.id!}`, incentive, {
      showLoader: true,
      showMessage: true,
      message: 'Incentive updated successfully'
    });
  }

  deleteProjectIncentive(incentiveId: string): Observable<unknown> {
    return this.#http.deleteData(`${this.baseUrl}/project-incentives/${incentiveId}`, {
      showLoader: true,
      showMessage: true,
      message: 'Incentive deleted successfully'
    });
  }

  getProjectLotRelationships(projectId: string): Observable<LotRelationship[]> {
    const payload = {
      projectIds: [projectId]
    };
    return this.#http
      .postData(`${this.baseUrl}/project/lots-with-neighbors`, { showLoader: true, showMessage: false }, payload)
      .pipe(map((res: any) => res ?? []));
  }

  importProjectLotRelationships(projectId: string, file: File): Observable<unknown> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.#http.postData(
      `${this.baseUrl}/project/${projectId}/lot-neighbor-map/import`,
      { showLoader: true, showMessage: true, message: 'Lot relationships imported successfully', showError: false },
      formData
    );
  }

  // getProjectSitingAnalysis(projectId: string): Observable<SitingAnalysis> {
  //   return this.#http
  //     .getData(`${this.baseUrl}/project/${projectId}/siting-analysis`, { showLoader: true, showMessage: false })
  //     .pipe(map((res: any) => res ?? ({} as SitingAnalysis)));
  // }
  getProjectSitingAnalysis(projectId: string): Observable<SitingAnalysis> {
    return this.#http
      .getData(`${this.baseUrl}/Project/${projectId}/lots/models-elevations`, {
        showLoader: true,
        showMessage: false
      })
      .pipe(
        map((res: any) => {
          // Supports wrapped response shape: { success, message, data, errors }
          const payload = res?.data ?? res;
          return (payload ?? ({} as SitingAnalysis)) as SitingAnalysis;
        })
      );
  }

  importProjectSitingAnalysis(projectId: string, file: File): Observable<unknown> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.#http.postData(
      `${this.baseUrl}/project/${projectId}/siting-analysis/import`,
      { showLoader: true, showMessage: true, showError: false, message: 'Siting analysis imported successfully' },
      formData
    );
  }

  exportProjectSitingAnalysis(projectId: string, fileName: string = 'siting-analysis.xlsx'): Observable<unknown> {
    if (!projectId || projectId === 'new') {
      return of(undefined);
    }
    return this.#http.downloadReportFile(
      `${this.baseUrl}/Project/${projectId}/siting-analysis/sample-file`,
      { showLoader: true, showMessage: false },
      null,
      fileName
    );
  }
  exportProjectLotRelationships(projectId: string): Observable<unknown> {
    if (!projectId || projectId === 'new') {
      return of(undefined);
    }
    return this.#http.downloadReportFile(
      `${this.baseUrl}/Project/${projectId}/lots-with-neighbors/sample-file`,
      { showLoader: true, showMessage: false },
      null,
      'lot-neighbor-map.xlsx'
    );
  }
}
